import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  WmsPersonnelPermissions,
  wmsDefaultPersonnelPermissions,
  WmsScreenType,
} from '../services/wmsPersonnelPermissionService';

export interface WmsPermissions {
  canPicking: boolean;
  canPacking: boolean;
  canReceiving: boolean;
  canCycleCount: boolean;
  pickingScreenId: string | null;
  packingScreenId: string | null;
  receivingScreenId: string | null;
  cycleCountScreenId: string | null;
}

export interface WmsCashierSession {
  cashierId: number;
  cashierName: string;
  personnelId?: string;
  personnelName?: string;
  email?: string;
  screenType: string;
  screenId: string;
  wmsPermissions: WmsPermissions;
  actionPermissions?: WmsPersonnelPermissions;
  loginTime: string;
}

const SESSION_KEY = 'wmsCashierSession';

const buildWmsPermissions = (screenType: WmsScreenType, screenId: string): WmsPermissions => ({
  canPicking: screenType === 'picking',
  canPacking: screenType === 'packing',
  canReceiving: screenType === 'receiving',
  canCycleCount: screenType === 'cyclecount',
  pickingScreenId: screenType === 'picking' ? screenId : null,
  packingScreenId: screenType === 'packing' ? screenId : null,
  receivingScreenId: screenType === 'receiving' ? screenId : null,
  cycleCountScreenId: screenType === 'cyclecount' ? screenId : null,
});

// Aynı sekmedeki tüm useWmsSession instance'larını senkron tutmak için
// modul seviyesinde paylasilan tek bir state ve dinleyici listesi.
const readStoredSession = (): WmsCashierSession | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? (JSON.parse(stored) as WmsCashierSession) : null;
  } catch {
    return null;
  }
};

let sharedSession: WmsCashierSession | null = readStoredSession();
const sessionListeners = new Set<(s: WmsCashierSession | null) => void>();

const broadcastSession = (next: WmsCashierSession | null) => {
  sharedSession = next;
  sessionListeners.forEach((listener) => listener(next));
};

const writeSession = (next: WmsCashierSession | null) => {
  if (next === null) {
    sessionStorage.removeItem(SESSION_KEY);
  } else {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(next));
  }
  broadcastSession(next);
};

const useWmsSession = () => {
  const { logout: firebaseLogout } = useAuth();
  const [session, setSession] = useState<WmsCashierSession | null>(sharedSession);

  useEffect(() => {
    sessionListeners.add(setSession);

    // Hook ilk mount edildiginde modul state'i ile yerel state arasinda
    // bir tutarsizlik kalmasin (ornegin onceki sayfa unmount sonrasi).
    if (sharedSession !== session) {
      setSession(sharedSession);
    }

    const handleStorage = (e: StorageEvent) => {
      if (e.key === SESSION_KEY) {
        const next = e.newValue ? (JSON.parse(e.newValue) as WmsCashierSession) : null;
        broadcastSession(next);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      sessionListeners.delete(setSession);
      window.removeEventListener('storage', handleStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback((data: WmsCashierSession) => {
    writeSession(data);
  }, []);

  const setPersonnelSession = useCallback((data: {
    personnelId: string;
    personnelName: string;
    email: string;
    screenType: WmsScreenType;
    screenId: string;
    actionPermissions?: WmsPersonnelPermissions;
  }) => {
    const next: WmsCashierSession = {
      cashierId: 0,
      cashierName: data.personnelName,
      personnelId: data.personnelId,
      personnelName: data.personnelName,
      email: data.email,
      screenType: data.screenType,
      screenId: data.screenId,
      wmsPermissions: buildWmsPermissions(data.screenType, data.screenId),
      actionPermissions: data.actionPermissions || wmsDefaultPersonnelPermissions,
      loginTime: new Date().toISOString(),
    };
    writeSession(next);
  }, []);

  const logout = useCallback(async () => {
    writeSession(null);
    try {
      await firebaseLogout();
    } catch {
      // Ignore logout errors; local session is already cleared.
    }
  }, [firebaseLogout]);

  const isLoggedIn = session !== null;

  return { session, login, logout, isLoggedIn, setPersonnelSession };
};

export default useWmsSession;
